import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Preview as PreviewIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, startOfYear } from "date-fns";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";
import { formatCurrency } from "../../../utils";
import {
  PREVIEW_FRAME_HEIGHT_SPLIT,
  PREVIEW_FRAME_HEIGHT_STACKED,
  PREVIEW_PANE_CHROME_HEIGHT,
  PREVIEW_STACK_BREAKPOINT_PX,
} from "../constants/previewLayout";

interface VatClient {
  clientId: string;
  clientName: string;
  unitRef: string;
  developmentName: string;
  transactionCount: number;
  vatBalance: number;
}

interface StatementLog {
  id: string;
  debtorId: string;
  debtorName: string;
  sentTo: string;
  period: string;
  fromDate?: string;
  toDate?: string;
  status: "sent" | "failed" | "pending";
  errorMessage?: string;
  statementType?: string;
  createdAt: string;
}

const VatStatements: React.FC = () => {
  const isStackedPreview = useMediaQuery(
    `(max-width:${PREVIEW_STACK_BREAKPOINT_PX}px)`,
  );
  const previewFrameHeight = isStackedPreview
    ? PREVIEW_FRAME_HEIGHT_STACKED
    : PREVIEW_FRAME_HEIGHT_SPLIT;
  const previewPaneHeight = previewFrameHeight + PREVIEW_PANE_CHROME_HEIGHT;

  const [clients, setClients] = useState<VatClient[]>([]);
  const [logs, setLogs] = useState<StatementLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState<Date | null>(
    startOfYear(new Date()),
  );
  const [toDate, setToDate] = useState<Date | null>(new Date());

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  const [historyDialog, setHistoryDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewClient, setPreviewClient] = useState<VatClient | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDownloading, setPreviewDownloading] = useState(false);
  const [previewEmailing, setPreviewEmailing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/vat-statements/clients`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error("Error fetching VAT clients:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      setLogsLoading(true);
      const token = localStorage.getItem("vms_access_token");
      const response = await fetch(
        `${config.API_BASE_URL}/statements/logs?limit=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        // Filter to show only VAT statement logs
        const vatLogs = data.filter(
          (log: StatementLog) => log.statementType === "vat",
        );
        setLogs(vatLogs);
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchLogs();
  }, [fetchClients, fetchLogs]);

  const createStatementParams = () => {
    const params = new URLSearchParams();
    if (fromDate) params.append("fromDate", format(fromDate, "yyyy-MM-dd"));
    if (toDate) params.append("toDate", format(toDate, "yyyy-MM-dd"));
    return params;
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const clearPreviewUrl = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handleDownloadPdf = async (clientId: string, clientName: string) => {
    try {
      setDownloading(clientId);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${clientId}/vat-statement/pdf?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const blob = await response.blob();
        downloadBlob(
          blob,
          `VAT_Statement_${clientName.replace(/\s+/g, "_")}.pdf`,
        );
      } else {
        setSnackbar({
          open: true,
          message: "Failed to generate PDF",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      setSnackbar({
        open: true,
        message: "Error downloading PDF",
        severity: "error",
      });
    } finally {
      setDownloading(null);
    }
  };

  const handleSendEmail = async (clientId: string, clientName: string) => {
    try {
      setSending(clientId);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${clientId}/vat-statement/email?${params}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        setSnackbar({
          open: true,
          message: `VAT Statement sent to ${result.sentTo}`,
          severity: "success",
        });
        fetchLogs();
      } else {
        const error = await response.json();
        setSnackbar({
          open: true,
          message: error.message || "Failed to send email",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      setSnackbar({
        open: true,
        message: "Error sending email",
        severity: "error",
      });
    } finally {
      setSending(null);
    }
  };

  const handleOpenPreview = async (client: VatClient) => {
    try {
      setPreviewClient(client);
      setPreviewDialog(true);
      setPreviewLoading(true);
      clearPreviewUrl();

      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${client.clientId}/vat-statement/pdf?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to load VAT preview");
      }

      const blob = await response.blob();
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to load preview",
        severity: "error",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreviewDialog = () => {
    setPreviewDialog(false);
    setPreviewClient(null);
    setPreviewLoading(false);
    setPreviewDownloading(false);
    setPreviewEmailing(false);
    clearPreviewUrl();
  };

  const handlePreviewDownload = async () => {
    if (!previewClient) return;

    try {
      setPreviewDownloading(true);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${previewClient.clientId}/vat-statement/pdf?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download VAT statement");
      }

      const blob = await response.blob();
      downloadBlob(
        blob,
        `VAT_Statement_${previewClient.clientName.replace(/\s+/g, "_")}.pdf`,
      );
      setSnackbar({
        open: true,
        message: "VAT statement downloaded",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to download VAT statement",
        severity: "error",
      });
    } finally {
      setPreviewDownloading(false);
    }
  };

  const handlePreviewEmail = async () => {
    if (!previewClient) return;

    try {
      setPreviewEmailing(true);
      const token = localStorage.getItem("vms_access_token");
      const params = createStatementParams();

      const response = await fetch(
        `${config.API_BASE_URL}/debtors/${previewClient.clientId}/vat-statement/email?${params}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to send VAT statement");
      }

      const result = await response.json();
      setSnackbar({
        open: true,
        message: `VAT Statement sent to ${result.sentTo}`,
        severity: "success",
      });
      fetchLogs();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to send VAT statement",
        severity: "error",
      });
    } finally {
      setPreviewEmailing(false);
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.unitRef.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.developmentName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Summary stats
  const totalVatDue = clients.reduce(
    (sum, c) => sum + (c.vatBalance > 0 ? c.vatBalance : 0),
    0,
  );
  const totalVatCredit = clients.reduce(
    (sum, c) => sum + (c.vatBalance < 0 ? Math.abs(c.vatBalance) : 0),
    0,
  );
  const clientsWithBalance = clients.filter((c) => c.vatBalance > 0).length;

  return (
    <FinanceContainer title="VAT Statements">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              VAT Statement Manager
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => {
                  fetchLogs();
                  setHistoryDialog(true);
                }}
              >
                History
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchClients}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <ReceiptIcon sx={{ fontSize: 40, color: "primary.main" }} />
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {clients.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Clients with VAT Transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <TrendingUpIcon sx={{ fontSize: 40, color: "error.main" }} />
                  <Typography variant="h6" sx={{ mt: 1, color: "error.main" }}>
                    {formatCurrency(totalVatDue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total VAT Due ({clientsWithBalance} clients)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent sx={{ textAlign: "center" }}>
                  <TrendingDownIcon
                    sx={{ fontSize: 40, color: "success.main" }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ mt: 1, color: "success.main" }}
                  >
                    {formatCurrency(totalVatCredit)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total VAT Credit
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by name, unit or project..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(date) => setFromDate(date)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(date) => setToDate(date)}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredClients.length} of {clients.length} clients
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Clients Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.dark" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Client Name
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Unit
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Project
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="center"
                  >
                    Transactions
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="right"
                  >
                    VAT Balance
                  </TableCell>
                  <TableCell
                    sx={{ color: "white", fontWeight: "bold" }}
                    align="center"
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No clients with VAT transactions found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow
                      key={client.clientId}
                      hover
                      sx={{
                        bgcolor:
                          client.vatBalance > 0
                            ? "rgba(244, 67, 54, 0.05)"
                            : undefined,
                      }}
                    >
                      <TableCell>
                        <Typography fontWeight="medium">
                          {client.clientName}
                        </Typography>
                      </TableCell>
                      <TableCell>{client.unitRef || "-"}</TableCell>
                      <TableCell>{client.developmentName || "-"}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={client.transactionCount}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          fontWeight="bold"
                          color={
                            client.vatBalance > 0
                              ? "error.main"
                              : client.vatBalance < 0
                              ? "success.main"
                              : "text.primary"
                          }
                        >
                          {formatCurrency(client.vatBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Download PDF">
                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleDownloadPdf(
                                client.clientId,
                                client.clientName,
                              )
                            }
                            disabled={
                              downloading === client.clientId ||
                              sending === client.clientId
                            }
                          >
                            {downloading === client.clientId ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PdfIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preview VAT Statement">
                          <IconButton
                            color="info"
                            onClick={() => handleOpenPreview(client)}
                            disabled={
                              sending === client.clientId ||
                              downloading === client.clientId ||
                              (previewLoading &&
                                previewClient?.clientId === client.clientId)
                            }
                          >
                            {previewLoading &&
                            previewClient?.clientId === client.clientId ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PreviewIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send via Email">
                          <span>
                            <IconButton
                              color="secondary"
                              onClick={() =>
                                handleSendEmail(
                                  client.clientId,
                                  client.clientName,
                                )
                              }
                              disabled={
                                sending === client.clientId ||
                                downloading === client.clientId
                              }
                            >
                              {sending === client.clientId ? (
                                <CircularProgress size={20} />
                              ) : (
                                <EmailIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={previewDialog}
            onClose={() =>
              !previewLoading && !previewDownloading && !previewEmailing
                ? closePreviewDialog()
                : undefined
            }
            maxWidth={isStackedPreview ? "md" : "lg"}
            fullWidth
          >
            <DialogTitle>
              Preview VAT Statement
              {previewClient ? ` - ${previewClient.clientName}` : ""}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review the VAT PDF before downloading or emailing.
              </Typography>
              <Paper
                variant="outlined"
                sx={{ height: previewPaneHeight, p: 1 }}
              >
                {previewLoading ? (
                  <Box
                    sx={{
                      height: previewFrameHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : previewUrl ? (
                  <Box
                    component="iframe"
                    src={previewUrl}
                    title="VAT Statement Preview"
                    sx={{
                      width: "100%",
                      height: previewFrameHeight,
                      border: 0,
                    }}
                  />
                ) : (
                  <Alert severity="warning">Preview is unavailable.</Alert>
                )}
              </Paper>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={closePreviewDialog}
                disabled={
                  previewLoading || previewDownloading || previewEmailing
                }
              >
                Close
              </Button>
              <Button
                variant="outlined"
                startIcon={
                  previewDownloading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handlePreviewDownload}
                disabled={
                  previewLoading || previewDownloading || previewEmailing
                }
              >
                {previewDownloading ? "Downloading..." : "Download VAT"}
              </Button>
              <Button
                variant="contained"
                startIcon={
                  previewEmailing ? (
                    <CircularProgress size={20} />
                  ) : (
                    <EmailIcon />
                  )
                }
                onClick={handlePreviewEmail}
                disabled={
                  previewLoading || previewDownloading || previewEmailing
                }
              >
                {previewEmailing ? "Sending..." : "Email VAT"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* History Dialog */}
          <Dialog
            open={historyDialog}
            onClose={() => setHistoryDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>VAT Statement History</DialogTitle>
            <DialogContent>
              {logsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : logs.length === 0 ? (
                <Alert severity="info">No VAT statements sent yet</Alert>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Sent To</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {new Date(log.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{log.debtorName}</TableCell>
                          <TableCell>{log.sentTo}</TableCell>
                          <TableCell>{log.period}</TableCell>
                          <TableCell>
                            {log.status === "sent" ? (
                              <Chip
                                icon={<CheckIcon />}
                                label="Sent"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Tooltip title={log.errorMessage || "Failed"}>
                                <Chip
                                  icon={<ErrorIcon />}
                                  label="Failed"
                                  size="small"
                                  color="error"
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHistoryDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </LocalizationProvider>
    </FinanceContainer>
  );
};

export default VatStatements;
